// ─────────────────────────────────────────────────────────────────────────────
// CORN APPRAISAL WORKSHEET — the rules for reading one, and for CHECKING it
// ─────────────────────────────────────────────────────────────────────────────
// An appraisal is the production-to-count on acres that will never cross a
// scale: standing corn chopped for silage, or an acre destroyed by something
// the policy does not cover. The number on this sheet goes onto a federal
// claim, so the reader is built around one property:
//
//   ⚠ THE SHEET CHECKS ITSELF. VERIFY IT — DO NOT TRUST THE EXTRACTION.
//
// Every worksheet prints its own arithmetic: the sample weights, the plot
// count, the yield factor, the per-stage subtotal, the acre appraisal and the
// acres. So a reader misreading 206.6 as 20.66 is caught by multiplication
// rather than by somebody eyeballing a PDF. Same doctrine as the target
// sheet's `cash = futures + basis` — a document that states its own inputs can
// refuse a pen slip while the paper is still in your hand.
//
// TWO FORMS ARE IN THE BOOK AND THEY DISAGREE ABOUT WHAT PART I MEANS:
//   • NCIS-M 215 "Weight Method Appraisal Worksheet" (R&H, underwritten ACE)
//       PART I  = MATURE ear corn / popcorn / grain sorghum / SILAGE.
//                 Its yield column is "PER ACRE YIELD (CIRCLE ONE)
//                 BUSHELS / TONS / POUNDS" — Part I is NOT the silage part.
//       PART II = MATURITY LINE WEIGHT METHOD, ear corn from milk stage to
//                 40% moisture. Always a CORN (or popcorn) per-acre figure.
//   • NAU Country "Corn Sorghum Appraisal"
//       Part II = mature line weight (grain bu/ac), Part I = silage ton/ac.
//
// Reading the NCIS Part I as silage — which the first version of this reader
// did, because it was written against the NAU form — turns an EAR CORN
// appraisal in bushels into tons. That is the never-blend-units-of-measure
// rule this repo enforces everywhere else, on the one document where the
// number becomes a grower's production of record.

// WHAT THE UNIT ACTUALLY DEPENDS ON: box 7, "CIRCLE APPRAISAL CODE". Five
// codes, and the choice is a PEN CIRCLE — every one of them is present in the
// page's text layer whether or not it was picked. A reader that scans text
// alone cannot tell which was circled and must say so rather than choose.
export const APPRAISAL_CODE = {
  EC:  { label: "Ear Corn",             unit: "BU"  },
  PEC: { label: "Popcorn",              unit: "LB"  },
  CS:  { label: "Corn Silage",          unit: "TON" },
  GS:  { label: "Grain Sorghum",        unit: "BU"  },
  GSS: { label: "Grain Sorghum Silage", unit: "TON" },
};

// Part II is the maturity-line method and its factors are PREPRINTED on the
// form, per stage and per sample size. Held here to CROSS-CHECK what was read
// off the page — never to overwrite it. A form revision may restate them, and
// silently imposing our copy would correct a right sheet to a wrong one.
export const PART2_YIELD_FACTOR = {
  "1/4":      { corn: { 100: 1.148, 1000: 11.48 }, popcorn: { 100: 40.0, 1000: 400.0 } },
  "1/2":      { corn: { 100: 1.057, 1000: 10.57 }, popcorn: { 100: 42.0, 1000: 420.0 } },
  "3/4":      { corn: { 100: 1.009, 1000: 10.09 }, popcorn: { 100: 45.0, 1000: 450.0 } },
  "Doughy":   { corn: { 100: 1.052, 1000: 10.52 }, popcorn: { 100: 47.0, 1000: 470.0 } },
  "Extended": { corn: { 100: 1.187, 1000: 11.87 }, popcorn: { 100: 59.0, 1000: 590.0 } },
};

// Tolerance for "does this reproduce the printed figure". The sheet prints to
// a fixed number of decimals, so the comparison has to allow exactly the
// rounding the form performed and nothing more — half an ulp of the value AS
// WRITTEN. Re-deriving precision after Number() widens it tenfold ("0.10"
// parses to 0.1, one decimal, not two), which is how an invented figure passes
// as a rounding of a real one.
export function halfUlp(written) {
  const s = String(written == null ? "" : written).trim();
  const dot = s.indexOf(".");
  const dp = dot < 0 ? 0 : s.length - dot - 1;
  return 0.5 * Math.pow(10, -dp) + 1e-9;
}

// Does `got` reproduce the figure the sheet PRINTED, at the sheet's precision?
export function agrees(got, printed) {
  if (got == null || printed == null) return null;
  const p = Number(printed);
  if (!Number.isFinite(got) || !Number.isFinite(p)) return null;
  return Math.abs(got - p) <= halfUlp(printed);
}

// ── The check ────────────────────────────────────────────────────────────────
// Returns { status, checks[], notes[] }. status is one of:
//   "verified"    every arithmetic identity the field supplied reproduces
//   "unchecked"   the field carries a yield and acres but no working to check
//   "failed"      an identity the field DID supply does not reproduce
// A field that cannot be checked is never called verified, and a field that
// fails is never silently corrected — both are reported and the row is KEPT,
// because a refused appraisal reads as "no production on these acres", which
// is the one answer that is certainly wrong.
export function checkAppraisalField(f, sheet) {
  const checks = [];
  const notes = [];
  const num = (v) => (v == null || v === "" ? null : Number(v));

  const acres = num(f.acres);
  const perAcre = num(f.appraised_yield);

  // 1. Part II: total sample weight × yield factor = the per-stage appraisal.
  for (const s of f.stages || []) {
    const tw = num(s.total_weight), yf = num(s.yield_factor), st = num(s.stage_appraisal);
    if (tw == null || yf == null || st == null) continue;
    const ok = agrees(tw * yf, s.stage_appraisal);
    if (ok !== null) {
      checks.push({ rule: "stage = weight x factor", stage: s.stage, ok,
        got: +(tw * yf).toFixed(4), printed: st });
    }
    // Cross-check the factor against the form's own preprinted table. A
    // disagreement is REPORTED, never corrected — see PART2_YIELD_FACTOR.
    const row = PART2_YIELD_FACTOR[s.stage];
    const frac = num(s.fraction_denominator);
    if (row && (frac === 100 || frac === 1000)) {
      // The appraisal code lives on the SHEET header, not usually on the row —
      // reading only the row put popcorn's 400.0 against corn's 11.48.
      const code = String(f.appraisal_code || (sheet && sheet.appraisal_code) || "").toUpperCase();
      const kind = code === "PEC" ? "popcorn" : "corn";
      const expect = row[kind][frac];
      if (Math.abs(yf - expect) > 1e-6) {
        notes.push(`stage ${s.stage} 1/${frac}: factor ${yf} is not the form's printed ${expect} (${kind}) — read, not corrected`);
      }
    }
  }

  // 2. Part II: the stage appraisals ÷ the representative plot count = the
  //    acre appraisal. This is the step that catches a transposed total.
  const totalAll = num(f.total_all_stages);
  const plots = num(f.total_plots);
  const printedAcre = f.acre_appraisal != null ? f.acre_appraisal : f.appraised_yield;
  if (totalAll != null && plots) {
    checks.push({ rule: "acre appraisal = total stages / plots",
      ok: agrees(totalAll / plots, printedAcre),
      got: +(totalAll / plots).toFixed(4), printed: printedAcre });
  }

  // 3. Does the stated per-stage set sum to the stated total?
  if (totalAll != null && (f.stages || []).length) {
    const parts = (f.stages || []).map((s) => num(s.stage_appraisal)).filter((v) => v != null);
    if (parts.length) {
      const sum = parts.reduce((a, b) => a + b, 0);
      checks.push({ rule: "total all stages = sum of stages", ok: agrees(sum, totalAll),
        got: +sum.toFixed(4), printed: totalAll });
    }
  }

  const ran = checks.filter((c) => c.ok !== null);
  const bad = ran.filter((c) => c.ok === false);
  const status = bad.length ? "failed" : ran.length ? "verified" : "unchecked";
  if (!ran.length && perAcre != null && acres != null) {
    notes.push("no working on the page to check against — yield and acres taken as printed");
  }
  return { status, checks: ran, notes, failed: bad.length };
}

// Appraised production for one field. The unit rides WITH the number: a corn
// silage field is tons and must never be added to a bushel total.
// ⚠ Number(null) is 0 and Number("") is 0, both finite — so a missing acreage
// or a missing yield silently became a real production of ZERO, which reads on
// screen as "these acres grew nothing" rather than "we did not read this".
// Test for the ABSENCE first; only then convert.
export function appraisedProduction(f) {
  if (f == null) return null;
  if (f.acres == null || f.acres === "" || f.appraised_yield == null || f.appraised_yield === "") return null;
  const acres = Number(f.acres), perAcre = Number(f.appraised_yield);
  if (!Number.isFinite(acres) || !Number.isFinite(perAcre)) return null;
  return +(acres * perAcre).toFixed(1);
}

// Which unit does this field's per-acre figure carry? Order matters: an
// explicit unit on the row wins; then the circled appraisal code; then, for a
// Part II row, corn bushels — Part II IS the ear-corn maturity method, so it
// has no other reading. A Part I row with no code and no unit is UNKNOWN and
// says so, because guessing here is the units bug this module exists to stop.
export function fieldUnit(f, sheet) {
  const explicit = String(f.yield_unit || "").toUpperCase();
  if (explicit === "BU" || explicit === "TON" || explicit === "LB") return explicit;
  const code = String(f.appraisal_code || (sheet && sheet.appraisal_code) || "").toUpperCase();
  if (APPRAISAL_CODE[code]) return APPRAISAL_CODE[code].unit;
  if (String(f.part || "").toUpperCase() === "II") return "BU";
  return null;
}

// One sheet -> storable rows. Bushels are the only unit that becomes grain
// production-to-count; tons and pounds are CARRIED and labelled, never summed
// into a bushel figure and never dropped.
// ⚠ THE SHEET HEADER'S FSA FARM NO IS NOT THE LINE'S. A Rain & Hail worksheet is one
// document covering whatever ground the adjuster walked that day, and that ground can
// span FSA farms — so a header FSN stamped on every line is the FIRST-WINS defect this
// repo has already paid for on type_code, unit_number and the P15 yield key.
//
// Measured on LANG FARMS LLC CY2026 (PROD-10644538.pdf, 4 lines): the header says 1543
// and the lines are 1543-2608-2, 6142-7622-1, 6142-7622-2, 1543-2608-7 — TWO farms. All
// four stored as 1543, which put 36.7 appraised acres against that farm's 12.9 insured
// (285%) and left 6142's 23.8 planted acres reading 0% appraised.
//
// The line's own `field_id` is FSN-TRACT-FIELD, and splitting it reconciles to the acre
// against policy_fields: 1543 -> 12.9 appraised on 12.9 planted, 6142 -> 23.8 on 23.8.
// Both 100%. A PER-LINE value always outranks a PER-DOCUMENT one, which is the same rule
// the batch quote printer follows when it reads its own `cov` parameter instead of state.
//
// STRICT PATTERN ONLY. Three all-digit parts; anything else is left alone and falls back
// to the header, because a field id we cannot decompose is not evidence of a farm number.
export function fsnTractField(fieldId) {
  const m = String(fieldId == null ? "" : fieldId).trim().match(/^(\d+)-(\d+)-(\d+)$/);
  return m ? { fsn: m[1], tract: m[2], field: m[3] } : null;
}

export function appraisalRows(parsed, opts) {
  const sheet = parsed || {};
  const o = opts || {};
  return (sheet.fields || []).map((f, i) => {
    const unit = fieldUnit(f, sheet);
    const isBu = unit === "BU";
    const prod = appraisedProduction(f);
    const chk = checkAppraisalField(f, sheet);
    const fieldId = f.field_id != null && String(f.field_id).trim() !== "" ? String(f.field_id).trim() : null;
    const ftf = fsnTractField(fieldId);
    return {
      farmer_id: o.farmerId,
      crop_year: o.cropYear,
      crop: "CORN",
      line_no: i + 1,
      county: (f.county || sheet.county || "").toUpperCase().replace(/^\d+-/, "").trim() || null,
      field_name: fieldId,
      // per-line explicit > the line's own FSN-TRACT-FIELD id > the sheet header (see
      // fsnTractField above — the header covers the document, not the line)
      fsn: f.fsa_farm_no != null ? String(f.fsa_farm_no)
         : (ftf ? ftf.fsn
         : (sheet.fsa_farm_no != null ? String(sheet.fsa_farm_no) : null)),
      tract: f.tract != null ? String(f.tract) : (ftf ? ftf.tract : null),
      field_no: f.field_no != null ? String(f.field_no) : (ftf ? ftf.field : null),
      unit_number: f.unit_number || sheet.unit_number || null,
      appraisal_code: f.appraisal_code || sheet.appraisal_code || null,
      yield_unit: unit,
      appraised_yield_bu_ac: isBu && f.appraised_yield != null ? Number(f.appraised_yield) : null,
      appraised_acres: f.acres != null ? Number(f.acres) : null,
      appraised_bu: isBu ? prod : null,
      appraised_qty: prod,
      source_part: f.part ? String(f.part).toUpperCase() : null,
      check_status: chk.status,
      check_detail: (chk.checks.length || chk.notes.length)
        ? JSON.stringify({ checks: chk.checks, notes: chk.notes }) : null,
      adjuster: sheet.adjuster || null,
      claim_number: sheet.claim_number || null,
      policy_number: sheet.policy_number || null,
      appraisal_date: sheet.appraisal_date || null,
      remarks: sheet.remarks || null,
      source_file: o.sourceFile || null,
      notes: isBu ? null
        : unit ? `${f.appraised_yield} ${unit}/ac${f.type ? ` (${f.type})` : ""}`
               : `${f.appraised_yield}/ac — UNIT NOT STATED (appraisal code not read)`,
    };
  });
}

// ── HOW THE LINE'S ACRES ARE INSURED ─────────────────────────────────────────
// Kevin, 2026-11-02, on HARKINS DAIRY FARM LLC NY-944-6076384-26: *"report it as is to
// match how it's insured"* and *"Should be able identify each silage field and grain
// field"*. Also: *"All farmers who insure their corn as grain that then gets chopped is to
// be reported as grain."*
//
// NAU's worksheet prints the coverage TYPE on every unit header — `Type: 016-Grain` or
// `Type: 026-Silage` — and the text reader stores the code as `type_code` (sql/124). It is
// the type the acres are INSURED under, not how they were harvested: a grain-insured field
// that was chopped is still 016 and still grain. Harkins' 17 Type 026 lines sum to 22.3 ac
// in Genesee and 207.8 ac in Wyoming against the policy's GSS 026 coverage of 22.26 and
// 207.7 — the worksheet and the policy agree.
//
// ⚠ A SILAGE-INSURED LINE IS STILL APPRAISED IN GRAIN BUSHELS (Part II is the ear-corn
// method), and those bushels are NOT grain production-to-count: a grain claim is the GSG
// guarantee against grain production. They are carried and labelled on their own, never
// added to the grain total — the never-merge-silage-and-grain rule.
//
// No code (the model path, an R&H form, a row stored before sql/124) is UNKNOWN and keeps
// the behaviour it had: counted as the bushels it says it is. Only a stated 026 moves.
//
// The silage spellings are claimCrops.js `isSilageType`'s, exactly (that module cannot be
// imported here: tools/check-appraisal-sheet.js evaluates this file's source on its own).
export const APPRAISAL_INSURED_TYPE = Object.freeze({
  "016": "grain", GSG: "grain", GRAIN: "grain",
  "026": "silage", GSS: "silage", SILAGE: "silage", "SILAGE-026": "silage",
});
export function appraisalInsuredType(row) {
  const c = String((row && row.type_code) == null ? "" : row.type_code).toUpperCase().trim();
  return APPRAISAL_INSURED_TYPE[c] || null;
}

// Roll a set of stored rows up for display. Units are kept APART: a book that
// prints one "total production" over bushels and tons has said nothing.
export function summarizeAppraisals(rows) {
  const out = { acres: 0, buAcres: 0, bu: 0, ton: 0, lb: 0, unknownUnitAcres: 0,
                // Bushels appraised on SILAGE-insured acres (type 026) — see
                // appraisalInsuredType. Kept out of `bu`/`buAcres`, which are grain.
                silageInsuredBu: 0, silageInsuredAcres: 0, silageInsuredLines: 0,
                rows: (rows || []).length, verified: 0, failed: 0, unchecked: 0 };
  for (const r of rows || []) {
    const ac = Number(r.appraised_acres) || 0;
    out.acres += ac;
    const u = String(r.yield_unit || "").toUpperCase();
    const q = Number(r.appraised_qty != null ? r.appraised_qty : r.appraised_bu);
    // buAcres is the acreage BEHIND the bushel total — the only acres a grain
    // production-to-count figure may be divided by or displayed beside. `acres`
    // spans every row including tons, so the two are not interchangeable.
    if (u === "BU" && Number.isFinite(q) && appraisalInsuredType(r) === "silage") {
      out.silageInsuredBu += q; out.silageInsuredAcres += ac; out.silageInsuredLines++;
    }
    else if (u === "BU" && Number.isFinite(q)) { out.bu += q; out.buAcres += ac; }
    else if (u === "TON" && Number.isFinite(q)) out.ton += q;
    else if (u === "LB" && Number.isFinite(q)) out.lb += q;
    else out.unknownUnitAcres += ac;
    if (r.check_status === "verified") out.verified++;
    else if (r.check_status === "failed") out.failed++;
    else out.unchecked++;
  }
  out.acres = +out.acres.toFixed(1);
  out.buAcres = +out.buAcres.toFixed(1);
  out.bu = +out.bu.toFixed(1);
  out.ton = +out.ton.toFixed(1);
  out.lb = +out.lb.toFixed(1);
  out.unknownUnitAcres = +out.unknownUnitAcres.toFixed(1);
  out.silageInsuredBu = +out.silageInsuredBu.toFixed(1);
  out.silageInsuredAcres = +out.silageInsuredAcres.toFixed(1);
  return out;
}

